import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Router, ActivatedRoute } from "@angular/router";
import { FormBuilder, Validators, AbstractControl } from "@angular/forms";

import { BehaviorSubject, of, Observable, from, forkJoin, Subscription } from "rxjs";
import { catchError, switchMap, finalize, map, tap } from "rxjs/operators";

//import { GridOptions, ColDef } from "@ag-grid-enterprise/all-modules";
import { GridOptions, ColDef, GridApi } from "ag-grid-community";

import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { LoaderService } from "../../../../common/services/loader.service";
import { ApplicationsService } from "../../../../applications/services/applications.service";
import { AuthService } from "../../../../services/auth.service";

import { SidePanelService } from "../../../../common/services/side-panel.service";
import { ApplicationNotesService } from "../../../../applications/services/application-notes.service";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { WellService } from "../../../../common/services/well.service";
import { IUploadFile, DocumentManagementService } from '../../../../common/services/document-management.service';
import { GisLocationService } from "../../../../gis/services/gis-location.service";
import { GisQueryService } from "../../../../gis/services/gis-query.service";
import { ApplicationConfigService } from "../../../../services/application-config.service";
import { ApplicationBaseComponent } from "../../../../applications/components/application-base.component";

import { SubHeader } from "../../../../core/models/sub-header.model";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";

import { GISConfig } from "../../../../gis/models/gis-config.model";
import { WorkflowData, InitiateWorkflowRequest, ApplicationType, BulkNAD83Data, ApplicationStateChangeRequest, ApplicationState } from "../../../../applications/models/applications.models";

import { ParseFileResponse, ParserError, ErrorVisibility, ErrorSeverity } from "../../../../builder/components/state/controls/config/data/file-parser-config/parser.model";


@Component({
    selector: 'app-upload-nad83-updates',
    templateUrl: './upload-nad83-updates.component.html',
    standalone: false
})
export class UploadNad83UpdatesComponent extends ApplicationBaseComponent implements OnInit, OnDestroy {

	private subheader: SubHeader = new SubHeader({ title: "Upload NAD83 Updates [APPNUMBER]", buttons: [{ name: "Cancel", method: () => this.home() }, { name: "Save", method: () => this.save() }, { name: "Submit", method: () => this.confirmSubmit() }, { name: "Download Sample File", method: () => this.downloadSample() }] });

	confirmSubmitMessage: string = "[APPNUMBER] will be submitted for review.  Are you sure you wish to submit this application?";
	confirmSubmitDialog: Dialog = new Dialog("", [new DialogButton("NO", false, () => { return; }), new DialogButton("Yes", true, () => { this.submit(); })]);
	submittedDialog: Dialog = new Dialog("Application #[APPNUMBER] has been submitted for review.", [new DialogButton("OK", true, () => { this.home(); })]);
	get allowFileDeletion(): boolean {
		return this.isParsing == false && this.fileCtrl != null && this.fileCtrl.enabled;
	}

	private config: GISConfig;

	private _data: BulkNAD83Data[];

	get data(): BulkNAD83Data[] {
		if (this._data != null) return this._data;
		if (this.form == null) return [];
		let ctrl = this.form.get('parsedData');
		if (ctrl == null || ctrl.value == null) return [];
		this._data = JSON.parse(ctrl.value);
		return this._data;
	}

	set data(value: BulkNAD83Data[]) {
		if (this.form == null) return;
		let ctrl = this.form.get('parsedData');
		if (ctrl == null) return;
		this._data = null;
		ctrl.setValue(JSON.stringify(value));
	}

	get dataColumns(): ColDef[] {
		return [
			{
				headerClass: "wrapped",
				headerName: "API #",
				headerTooltip: "API #",
				field: "apiNumber",
				tooltipField: "apiNumber",
				suppressMovable: true,
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				maxWidth: 125
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Latitude (NAD83)",
				headerTooltip: "Latitude (NAD83)",
				field: "latitude83",
				tooltipField: "latitude83",
				suppressMovable: true,
				filter: "agNumberColumnFilter",
				filterParams: this._formatter.defaultNumberFilterParams,
				maxWidth: 150
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Longitude (NAD83)",
				headerTooltip: "Longitude (NAD83)",
				field: "longitude83",
				tooltipField: "longitude83",
				suppressMovable: true,
				filter: "agNumberColumnFilter",
				filterParams: this._formatter.defaultNumberFilterParams,
				maxWidth: 150
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Source of Location",
				headerTooltip: "Source of Location",
				field: "sourceOfLocation",
				tooltipField: "sourceOfLocation",
				suppressMovable: true,
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams
			}
			
		] as ColDef[];
	}

	@ViewChild("downloadLink") public downloadLink: ElementRef;

	get errorGridColumns(): ColDef[] {
		return [
			{
				headerName: "Line",
				headerTooltip: "Line",
				field: "line",
				tooltipField: "line",
				suppressMovable: true,
				minWidth: 100,
				maxWidth: 100
			} as ColDef,
			{
				headerName: "Column",
				headerTooltip: "Column",
				field: "column",
				tooltipField: "column",
				suppressMovable: true,
				minWidth: 125,
				maxWidth: 125
			} as ColDef,
			{
				headerName: "Error",
				headerTooltip: "Error",
				field: "error",
				tooltipField: "error",
				suppressMovable: true
			} as ColDef
		];
	}

	errorGridOptions: GridOptions; 
	errorGridApi?: GridApi;
	private _errors: ParserError[];

	get errors(): ParserError[] {
		if (this._errors != null) return this._errors;
		if (this.form == null) return [];
		let ctrl = this.form.get('errors');
		if (ctrl == null || ctrl.value == null) return [];
		this._errors = JSON.parse(ctrl.value);
		return this._errors;
	}

	set errors(value: ParserError[]) {
		if (this.form == null) return;
		let ctrl = this.form.get('errors');
		if (ctrl == null) return;
		this._errors = null;
		ctrl.setValue(JSON.stringify(value));
	}

	get fileCtrl(): AbstractControl {
		if (this.form == null) return null;
		return <AbstractControl>this.form.get("files");
	}

	get files(): IUploadFile[] {
		if (this.fileCtrl == null) return [];
		return <IUploadFile[]>this.fileCtrl.value;
	}

	get hasFileListing(): boolean {
		return this.files != null && this.files.length > 0;
	}

	get isParsing(): boolean {
		return this.parsingInProgress.value;
	}

	endStates: ApplicationState[] = [ApplicationState.Approved, ApplicationState.Withdrawn];


	parsedGridOptions: GridOptions;
	parsedGridApi?: GridApi;
	private parsingInProgress: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	statusBar = {
		statusPanels: [
			{
				statusPanel: 'agFilteredRowCountComponent',
				align: 'right',
			},
			{
				statusPanel: 'agTotalRowCountComponent',
				align: 'right',
			}
		]
	};

	constructor(private route: ActivatedRoute, private _builder: FormBuilder, _router: Router, _subheaderService: SubHeaderService, _dialog: DynamicDialogService, _auth: AuthService, private _docService: DocumentManagementService, private _configService: ApplicationConfigService,
			_loader: LoaderService, _applications: ApplicationsService, _notes: ApplicationNotesService, _sidePanel: SidePanelService, private _wells: WellService, private _formatter: GridFormatterService, private _location: GisLocationService,
			private _queryService: GisQueryService) {
		super(_loader, _dialog, _subheaderService, _auth, _applications, _router, _sidePanel, _notes);
		this.fileFields = ['files'];
		//this.config = window["GIS.Conf"] as GISConfig; //get this coming from config service!
	}

	ngOnInit() {
		this.subheaderService.updateSubHeader(this.subheader);
		super.ngOnInit();
		if (this.route.snapshot.data.application) {
			this.application = this.route.snapshot.data.application;
			if (this.application.applicationState == ApplicationState.InitialReview) { //application is in review state; redirect to the review page
				let url = `/admin/bulk-nad83-update/review/${this.application.applicationNumber}`;
				this.router.navigate([url]);
			} else if (this.endStates.includes(this.application.applicationState)) {
				let url = `/admin/bulk-nad83-update/complete/${this.application.applicationNumber}`;
				this.router.navigate([url]);
			}
		}
		if (this.application == null) {
			this.subheader.title = this.subheader.title.replace('[APPNUMBER]', '');
		} else {
			this.subheader.title = this.subheader.title.replace('[APPNUMBER]', `- Application #${this.application.applicationNumber}`);
		}

		let subs$ = this._configService.getGISConfig().pipe(
			tap((conf: GISConfig) => {
				this.config = conf;
			})
		).subscribe({
			next: () => { },
			error: (err) => {
				this.handleError(err);
			}
		});
		this.subscription.add(subs$);

		this.buildForm(this.application);
		this.setupGrids();
	}

	buildForm(data: WorkflowData | null = null): void {
		this.form = this._builder.group({
			files: [[], [Validators.required, Validators.minLength(1)]],
			parsedData: [null, [Validators.required]],
			errors: [null, []]
		});
		if (data != null) {
			this.form = this.restoreFormData(this.form);
		}
		this.formReady$.next(true);
	}

	confirmSubmit(): void {
		if (this.form.valid) {
			let applicationNumber: string = (this.application != null) ? `Application ${this.application.applicationNumber}` : "This application";
			this.confirmSubmitDialog.message = this.confirmSubmitMessage.replace("[APPNUMBER]", applicationNumber);
			this.confirmSubmitDialog.toggleDialog(true);
			this.dialogService.updateDialog(this.confirmSubmitDialog);
		} else {
			this.dialogService.showFormInvalid();
		}
	}

	downloadSample(): void {
		this._loader.toggleLoader(true);
		const sample$ = this._docService.getSampleBulkNAD83UpdateFile().pipe()
			.subscribe(
				(data: any) => {
					this._loader.toggleLoader(false);
					const fileName = "bulk-nad83-sample.xlsx";
					const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
					const blob = new Blob([data], { type: fileType });
					const blobUrl = window.URL.createObjectURL(blob);
					const anchor = <HTMLAnchorElement>this.downloadLink.nativeElement;
					anchor.href = blobUrl;
					anchor.download = fileName;
					anchor.click();
				},
				(error) => {
					this.handleError(error);
				}
		);
		this.subscription.add(sample$);
	}

	fileAdded(evt: IUploadFile): void {
		if (evt != null) {
			const files: IUploadFile[] = this.files;
			const isNew = files.findIndex(l => l.fileName === evt.fileName) === -1;
			if (isNew) {
				evt.documentType = "DNR - OG - PRMT-RICS Additional Miscellaneous Flow Document";
				files.push(evt);
				this.fileCtrl.setValue(files);
			}
			this.parseFile(evt);
		}		
	}

	fileRemoved(evt: IUploadFile): void {
		const files: IUploadFile[] = this.files;
		const fileIndex = files.findIndex(l => l.fileName === evt.fileName);
		if (fileIndex !== -1) {
			files.splice(fileIndex, 1);
			this.fileCtrl.setValue(files);
		}	
		
	}

	parseFile(file: IUploadFile): void {
		this.parsingInProgress.next(true);
		let obs$ = this._docService.parseBulkNAD83UpdateRequest(file);
		const parse$ = obs$.pipe(
			catchError((error) => {
				this.handleError(error);
				return of(null);
			}),
			switchMap((parsedData: ParseFileResponse | null) => {
				let obs$ = of(parsedData);
				if (parsedData != null  && parsedData.data != null && parsedData.data.length > 0) {
					obs$ = this.queryGIS(parsedData.data).pipe(
						map((queried: (BulkNAD83Data | ParserError)[]) => {
							let response: ParseFileResponse = {
								data : [],
								errors : []
							} as ParseFileResponse;
							if (parsedData.errors != null && parsedData.errors.length > 0) {
								response.errors = parsedData.errors;
							}
							queried.forEach(q => {
								if ((q as any).apiNumber != null) {
									response.data.push(q as BulkNAD83Data)
								} else if ((q as any).line != null) {
									response.errors.push(q as ParserError);
								}
							});
							response.errors = response.errors.sort((a, b) => {
								if (a.line < b.line) return -1;
								if (a.line > b.line) return 1;
								if (a.column > b.column) return -1;
								if (a.column < b.column) return 1;
								return 0;
							})
							return response;
						})
					);
				}
				return obs$;
			}),
			finalize(() => {
				this.parsingInProgress.next(false);
				parse$.unsubscribe();
			})
		).subscribe(
			(response: ParseFileResponse | null) => {
				if (response != null) {
					this.data = response.data;
					this.errors = response.errors;
				}
			},
			(error) => {
				this.handleError(error);
			}
		);
		this.subscription.add(parse$);
	}

	queryGIS(responses: BulkNAD83Data[]): Observable<(BulkNAD83Data | ParserError)[]> {
		let obs$: Observable<BulkNAD83Data | ParserError>[] = [];
		responses.forEach(r => {
			let query = this.queryCounty(r);
			obs$.push(query);
		});
		return forkJoin(obs$);
	}

	queryCounty(response: BulkNAD83Data): Observable<BulkNAD83Data | ParserError> {
		let query: Observable<any> = from(this._queryService.resolveQueryAtPoint(this.config.yearlyGISDataUrl, "Counties", [response.latitude83, response.longitude83]))
			.pipe(
				map((gis: any) => {
					if (gis != null && gis.features != null && gis.features.length > 0) {
						let countyName = new String(gis.features[0].attributes["COUNTY"] || "").toLocaleLowerCase();
						let countyCode = new String(gis.features[0].attributes["FIPS"] || "").toLocaleLowerCase();
						let nameMatches = countyName == new String(response.countyName).toLocaleLowerCase() && countyCode == new String(response.countyCode).toLocaleLowerCase();
						let codeMatches = countyCode == response.countyCode;
						if (nameMatches || codeMatches) {
							return response;
						} else {
							let error = {
								line: response.line,
								column: "B & C",
								error: `Latitude ${response.latitude83}, Longitude ${response.longitude83} lie in county ${countyCode} - ${countyName} county.  Well is in ${response.countyCode} - ${response.countyName} County.`,
								severity: ErrorSeverity.Warning,
								visibility: ErrorVisibility.Any
							} as ParserError;
							return error;
						}
					} else {
						let error = {
							line: response.line,
							column: "B & C",
							error: `Latitude ${response.latitude83}, Longitude ${response.longitude83} does not lie in a verifiable County.`,
							severity: ErrorSeverity.Warning,
							visibility: ErrorVisibility.Any
						} as ParserError;
						return error;
					}
				})
		);


		
		return query;
	}

	save(): void {
		let obs$: Observable<WorkflowData>;
		if (this.application == null) {
			let req: InitiateWorkflowRequest = {
				applicationType: ApplicationType.BulkNAD83Update
			} as InitiateWorkflowRequest;
			obs$ = this.dataService.initiateApplication(req).pipe(
				switchMap((app: WorkflowData) => {
					this.application = app;
					return this.saveProgress();
				})
			);
		} else {
			obs$ = this.saveProgress();
		}
		this._loader.toggleLoader(true);
		let subs$ = obs$.pipe(
			finalize(() => {
				subs$.unsubscribe();
				this._loader.toggleLoader(false);
			})
		).subscribe(
			(app: WorkflowData) => {
				this.application = app;
				let msg = this.saveUnsubmittedMessage.replace("[XXX]", this.application.applicationNumber);
				this.doneForNowApplicationNumberDialog.message = msg;
				this.doneForNowApplicationNumberDialog.toggleDialog(true);
				this.dialogService.updateDialog(this.doneForNowApplicationNumberDialog);
			},
			(err) => {
				this.handleError(err);
			}
		);
		this.subscription.add(subs$);
	}

	setupGrids(): void {
		this.errorGridOptions = this._formatter.BuildDefaultGridOptionsWithColumns(this.errorGridColumns, false, true, true, true);
		this.parsedGridOptions = this._formatter.BuildDefaultGridOptionsWithColumns(this.dataColumns, false, true, true, true);
		this.parsedGridOptions.headerHeight = 50;
		this.parsedGridOptions.pagination = false;
		this.parsedGridOptions.onGridReady = (params) => {
			this.parsedGridApi = params.api;
		};
		this.parsedGridOptions.onFirstDataRendered = () => {
			if (this.parsedGridApi != null) {
				this.parsedGridApi.sizeColumnsToFit();
			}
		};
		this.errorGridOptions.pagination = false;
		this.errorGridOptions.onGridReady = (params) => {
			this.errorGridApi = params.api;
			this.errorGridApi.sizeColumnsToFit();
		};
		this.errorGridOptions.onFirstDataRendered = () => { 
			if (this.errorGridApi != null) {
				this.errorGridApi.sizeColumnsToFit();
			}
		 }
	}

	submit(): void {		
		let obs$: Observable<WorkflowData>;
		if (this.application == null) {
			let req: InitiateWorkflowRequest = {
				applicationType: ApplicationType.BulkNAD83Update
			} as InitiateWorkflowRequest;
			obs$ = this.dataService.initiateApplication(req).pipe(
				switchMap((app: WorkflowData) => {
					this.application = app;
					return this.saveProgress();
				})
			);
		} else {
			obs$ = this.saveProgress();
		}		

		obs$ = obs$.pipe(
			switchMap((app: WorkflowData) => {
				this.application = app;
				let destination: ApplicationState = ApplicationState.InitialReview;
				let request: ApplicationStateChangeRequest = {
					applicationId: app.id,
					currentState: app.applicationState,
					requestedState: destination
				} as ApplicationStateChangeRequest;
				return this.dataService.routeApplication(request);
			})
		);
		this._loader.toggleLoader(true);
		let subs$ = obs$.pipe()
			.subscribe(
				(app: WorkflowData) => {
					this.submittedDialog.message = this.submittedDialog.message.replace('[APPNUMBER]', app.applicationNumber);
					this._loader.toggleLoader(false);
					this.submittedDialog.toggleDialog(true);
					this.dialogService.updateDialog(this.submittedDialog);
				},
				(error) => {
					console.error(error);
					this._loader.toggleLoader(false);
					this.dialogService.showError();
				}
			);
		this.subscription.add(subs$);
	}
}
